import { useState, useEffect } from "react";
import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import { CircularProgress } from "@mui/material";
import CategoryCard from "../CategoryCard";
import MultiPropSelectModal from "../MultiPropSelectModal";
import SinglePropSelectModal from "../SinglePropSelectModal";
import {
  PrimaryAction,
  ReplicateContent,
  ReplicateHeader,
  ReplicateSection,
  ReplicateSelectionCard,
  ReplicateSubtitle,
  ReplicateTitle
} from "../Styles/InputStyles";

const TenantKYCModal = ({
  onClose,
  settingsData,
  editNotiSettingsMutate,
  editNotiSettingsLoading,
  property,
  propertyList,
  fetchNotifySettingsLoading,
  isDataLoaded,
  propertyParam,
  fetchNotiSettingsMutate
}) => {
  const [settings, setSettings] = useState({
    whatsApp: false,
    sms: false,
    rentReminder: false,
    onboardWelcome: false,
  });

  const [filteredProperties, setFilteredProperties] = useState([]);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showMultiSelectModal, setShowMultiSelectModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [isReplicating, setIsReplicating] = useState(false);
  const [isPropertyChanging, setIsPropertyChanging] = useState(false);

  const allProperties = propertyList || [];

  useEffect(() => {
    if (propertyList) {
      const filtered = propertyList?.filter(prop => prop.id !== 0) || [];
      setFilteredProperties(filtered);
    }
  }, [propertyList]);

  useEffect(() => {
    if (propertyList) {
      const filtered = propertyList?.filter(prop => prop.id !== 0) || [];
      setFilteredProperties(filtered);

      if (filtered.length > 0 && !selectedProperty) {
        let initialProperty;

        if (propertyParam && propertyParam !== 0) {
          initialProperty = filtered.find(prop => prop.id === propertyParam);
        }

        if (!initialProperty) {
          initialProperty = filtered[0];
        }

        setSelectedProperty(initialProperty);
        setSelectedPropertyId(initialProperty.id);

        if (fetchNotiSettingsMutate && initialProperty.id) {
          fetchNotiSettingsMutate(initialProperty.id);
        }
      }
    }
  }, [propertyList, propertyParam, fetchNotiSettingsMutate]);

  useEffect(() => {
    if (settingsData) {
      setSettings({
        whatsApp: settingsData?.whatsApp === 1,
        sms: settingsData?.sms === 1,
        rentReminder: settingsData?.rentReminder === 1,
        onboardWelcome: settingsData?.onboardWelcome === 1,
      });
    }
  }, [settingsData]);

  const toggleSetting = (key) => {
    const newValue = !settings[key];
    const updatedSettings = { ...settings, [key]: newValue };
    setSettings(updatedSettings);

    // Prepare payload with all settings
    const payload = {
      propIds: [selectedPropertyId],
      whatsApp: updatedSettings.whatsApp ? 1 : 0,
      sms: updatedSettings.sms ? 1 : 0,
      rentReminder: updatedSettings.rentReminder ? 1 : 0,
      onboardWelcome: updatedSettings.onboardWelcome ? 1 : 0,
    };

    editNotiSettingsMutate(payload);
  };

  const handleSelectedProperty = (selectedOption) => {
    const newPropertyId = selectedOption.id;

    if (newPropertyId === selectedPropertyId) {
      setSelectedProperty(selectedOption);
      return;
    }

    setIsPropertyChanging(true);
    setSelectedProperty(selectedOption);
    setSelectedPropertyId(newPropertyId);

    setSettings({
      whatsApp: false,
      sms: false,
      rentReminder: false,
      onboardWelcome: false,
    });

    if (fetchNotiSettingsMutate) {
      fetchNotiSettingsMutate(newPropertyId, {
        onSettled: () => {
          setIsPropertyChanging(false);
        }
      });
    } else {
      setIsPropertyChanging(false);
    }
  };

  const handleReplicateSettings = () => {
    if (selectedProperties.length === 0) {
      setShowMultiSelectModal(true);
      return;
    }

    setIsReplicating(true);

    // Prepare payload with all settings for multiple properties
    const payload = {
      propIds: selectedProperties,
      whatsApp: settings.whatsApp ? 1 : 0,
      sms: settings.sms ? 1 : 0,
      rentReminder: settings.rentReminder ? 1 : 0,
      onboardWelcome: settings.onboardWelcome ? 1 : 0,
    };

    editNotiSettingsMutate(payload, {
      onSettled: () => {
        setIsReplicating(false);
      }
    });
  };

  const handleMultiPropertySave = (selectedProps) => {
    setSelectedProperties(selectedProps);
    setShowMultiSelectModal(false);
  };

  const renderSelectedProperties = (selected = []) => {
    if (!selected.length) return "No properties selected";

    if (selected.length === allProperties.length) {
      return "All Properties";
    }

    const selectedPropertyNames = allProperties
      .filter((prop) => selected.includes(prop.id))
      .map((prop) => prop.name);

    if (!selectedPropertyNames.length) return "No properties selected";

    const maxToShow = 2;
    const namesToShow = selectedPropertyNames.slice(0, maxToShow);
    const remainingCount = selectedPropertyNames.length - maxToShow;

    return (
      <PropertyDisplayContainer>
        {namesToShow.join(", ")}
        {remainingCount > 0 && (
          <RemainingCount>+{remainingCount}</RemainingCount>
        )}
      </PropertyDisplayContainer>
    );
  };

  const showLoading = fetchNotifySettingsLoading || !isDataLoaded || isPropertyChanging;

  return (
    <div className="bg-white w-full h-full flex flex-col">
      <Header>
        <HeaderTitle>Notification Settings</HeaderTitle>
        <CloseButton onClick={onClose}>
          <MdClose color="white" size={24} />
        </CloseButton>
      </Header>

      {showLoading ? (
        <div className="flex items-center justify-center h-full">
          <CircularProgress />
        </div>
      ) : (
        <>
          <ContentContainer>
            <ContentWrapper>
              <Section>
                {/* Show property selector only when "All Properties" is selected */}
                {(property?.id === 0 || propertyParam === 0) && filteredProperties.length > 0 && (
                  <CategoryCard
                    title="Property"
                    selectedCategory={selectedProperty}
                    onClick={() => setShowPropertyModal(true)}
                  />
                )}

                <SettingsGrid>
                  <div className="mb-2">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                      Communication & Notification Channels
                    </p>
                  </div>

                  {/* WhatsApp Notifications */}
                  <SettingCard $active={settings.whatsApp}>
                    <SettingContent>
                      <SettingHeader>
                        <TitleRow>
                          <SettingTitle>WhatsApp Notifications</SettingTitle>
                        </TitleRow>
                        <SettingDescription>
                          Receive important updates and notifications through WhatsApp messages for instant communication.
                        </SettingDescription>
                      </SettingHeader>
                      <ToggleWrapper>
                        <Toggle
                          checked={settings.whatsApp}
                          onChange={() => toggleSetting("whatsApp")}
                          disabled={editNotiSettingsLoading || isPropertyChanging}
                        />
                      </ToggleWrapper>
                    </SettingContent>
                  </SettingCard>

                  {/* SMS Notifications */}
                  {/* <SettingCard $active={settings.sms}>
                    <SettingContent>
                      <SettingHeader>
                        <TitleRow>
                          <SettingTitle>SMS Notifications</SettingTitle>
                        </TitleRow>
                        <SettingDescription>
                          Get essential alerts and updates delivered directly to your mobile device via text messages.
                        </SettingDescription>
                      </SettingHeader>
                      <ToggleWrapper>
                        <Toggle
                          checked={settings.sms}
                          onChange={() => toggleSetting("sms")}
                          disabled={editNotiSettingsLoading || isPropertyChanging}
                        />
                      </ToggleWrapper>
                    </SettingContent>
                  </SettingCard> */}

                  {/* Tenant Onboarding Welcome */}
                  <SettingCard $active={settings.onboardWelcome}>
                    <SettingContent>
                      <SettingHeader>
                        <TitleRow>
                          <SettingTitle>Tenant Onboarding Welcome</SettingTitle>
                        </TitleRow>
                        <SettingDescription>
                          When enabled, a welcome message will be sent to new tenants when they join your property.
                        </SettingDescription>
                      </SettingHeader>
                      <ToggleWrapper>
                        <Toggle
                          checked={settings.onboardWelcome}
                          onChange={() => toggleSetting("onboardWelcome")}
                          disabled={editNotiSettingsLoading || isPropertyChanging}
                        />
                      </ToggleWrapper>
                    </SettingContent>
                  </SettingCard>

                  {/* Automated Payment Reminders */}
                  <SettingCard $active={settings.rentReminder}>
                    <SettingContent>
                      <SettingHeader>
                        <TitleRow>
                          <SettingTitle>Automated Payment Reminders</SettingTitle>
                        </TitleRow>
                        <SettingDescription>
                          Allow tenants to receive automated payment and due date reminders to ensure timely payments.
                        </SettingDescription>
                      </SettingHeader>
                      <ToggleWrapper>
                        <Toggle
                          checked={settings.rentReminder}
                          onChange={() => toggleSetting("rentReminder")}
                          disabled={editNotiSettingsLoading || isPropertyChanging}
                        />
                      </ToggleWrapper>
                    </SettingContent>
                  </SettingCard>
                </SettingsGrid>

                {/* Replicate Settings Section - Only show when multiple properties exist */}
                {filteredProperties.length > 1 && (
                  <ReplicateSection>
                    <ReplicateHeader>
                      <ReplicateTitle>Apply Settings to Other Properties</ReplicateTitle>
                      <ReplicateSubtitle>
                        Instantly copy these notification settings across selected properties
                      </ReplicateSubtitle>
                    </ReplicateHeader>

                    <ReplicateContent>
                      <ReplicateSelectionCard
                        type="button"
                        onClick={() => setShowMultiSelectModal(true)}
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            Select Properties
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {selectedProperties.length > 0
                              ? renderSelectedProperties(selectedProperties)
                              : "Choose one or more properties"}
                          </p>
                        </div>
                        <span className="text-primary-color text-sm font-medium">
                          {selectedProperties.length > 0 ? "Edit" : "Select"}
                        </span>
                      </ReplicateSelectionCard>

                      <PrimaryAction
                        onClick={handleReplicateSettings}
                        disabled={editNotiSettingsLoading || selectedProperties.length === 0 || isReplicating || isPropertyChanging}
                      >
                        {isReplicating
                          ? "Applying..."
                          : selectedProperties.length > 0
                            ? `Apply to ${selectedProperties.length} Properties`
                            : "Select Properties First"}
                      </PrimaryAction>
                    </ReplicateContent>
                  </ReplicateSection>
                )}
              </Section>
            </ContentWrapper>

            {/* Single Property Selection Modal */}
            <SinglePropSelectModal
              isOpen={showPropertyModal}
              onClose={() => setShowPropertyModal(false)}
              onSelect={handleSelectedProperty}
              options={filteredProperties}
              selectedProperty={selectedProperty}
            />

            {/* Multi Property Selection Modal */}
            <MultiPropSelectModal
              isOpen={showMultiSelectModal}
              onClose={() => setShowMultiSelectModal(false)}
              onSelect={handleMultiPropertySave}
              options={filteredProperties}
              selectedProperties={selectedProperties}
            />
          </ContentContainer>
        </>
      )}
    </div>
  );
};

// Styled Components
const ContentContainer = tw.div`flex-1 overflow-y-auto`;
const ContentWrapper = tw.div`p-6`;
const Section = tw.div`space-y-6`;
const Header = tw.div`flex items-center justify-between bg-[#0078BD] p-4`;
const HeaderTitle = tw.h1`text-white text-lg font-medium`;
const CloseButton = tw.button``;
const PropertyDisplayContainer = tw.div`flex items-center gap-1 truncate`;
const RemainingCount = tw.span`text-gray-500 ml-1 font-medium`;
const SettingsGrid = tw.div`space-y-4`;

const SettingCard = tw.div`
  relative rounded-xl border p-4 transition-all duration-200 bg-white
  ${(p) =>
    p.$active
      ? "border-gray-300 shadow-sm"
      : "border-gray-200 hover:border-gray-300 hover:shadow-sm"}
`;

const SettingContent = tw.div`
  flex items-center justify-between gap-4
`;

const SettingHeader = tw.div`flex-1`;

const TitleRow = tw.div`
  flex items-center gap-2 mb-1
`;

const SettingTitle = tw.h3`
  text-sm font-semibold text-gray-800
`;

const SettingDescription = tw.p`
  text-xs text-gray-500 leading-relaxed
`;

const ToggleWrapper = tw.div`
  flex items-center justify-center
`;

const Toggle = ({ checked, onChange, disabled }) => (
  <ToggleLabel disabled={disabled}>
    <ToggleInput
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
    <ToggleSlider checked={checked} disabled={disabled}>
      <ToggleThumb checked={checked} />
    </ToggleSlider>
  </ToggleLabel>
);

const ToggleLabel = tw.label`${props => props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'} relative inline-flex items-center`;
const ToggleInput = tw.input`sr-only peer`;
const ToggleSlider = tw.div`relative w-12 h-6 rounded-full transition-colors duration-200 ease-in-out ${props => props.disabled ? 'bg-gray-200 cursor-not-allowed' : props.checked ? 'bg-primary-color' : 'bg-gray-300'}`;
const ToggleThumb = tw.div`absolute top-0.5 left-0.5 w-5 h-5 bg-white border border-gray-300 rounded-full shadow-sm transition-transform duration-200 ease-in-out ${props => props.checked ? 'transform translate-x-6' : ''}`;

export default TenantKYCModal;